import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined'
import { Stack } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import type { Identifier, XYCoord } from 'dnd-core'
import type { FC } from 'react'
import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { styles } from './styles'

export interface CardProps {
  id: string
  text: string
  index: number
  moveCard: (dragIndex: number, hoverIndex: number) => void
  selected: boolean
  selectCard: (index: number) => void
}

interface DragItem {
  index: number
  id: string
  type: string
}

export const SortableItem: FC<CardProps> = ({
  id,
  text,
  index,
  moveCard,
  selected,
  selectCard,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { id, index }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const opacity = isDragging ? 0 : 1
  drag(drop(ref))
  return (
    <div
      style={{ ...styles.sortableItem, opacity }}
      ref={ref}
      data-handler-id={handlerId}
    >
      <Stack gap={1} direction={'row'} alignItems={'center'}>
        <Checkbox
          color="primary"
          // checked={allSelected}
          // onChange={allCheckboxHandle}
          checked={selected}
          onChange={() => selectCard(index)}
        />
        {text}
      </Stack>
      <div style={styles.dragItem}>
        <DragIndicatorOutlinedIcon />
      </div>
    </div>
  )
}
